"use client";

import { useCallback, useRef } from "react";
import QRCode from "qrcode";
import { apiFetch, apiFetchAuthed, AuthExpiredError } from "@/lib/api";
import { ENV_PREFIX } from "@/lib/query-keys";
import { eventDetailLog as log } from "@/lib/logger";
import { buildEventPath } from "@/lib/routes";
import { useToast } from "@/components/ui/Toast";

import type { PublicEvent, RsvpStatus } from "@/app/(events)/[id]/[slug]/components/EventTypes";
import type { useEventPage } from "./useEventPage";

/* Shared utility: detect in-app browsers */
export function isInAppBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Instagram/.test(ua) || /FBAN/.test(ua) || /FBAV/.test(ua) || /FB_IAB/.test(ua) || /Line\//.test(ua) || /WhatsApp/.test(ua) || /Twitter/.test(ua) || /Snapchat/.test(ua) || /musical_ly|TikTok/.test(ua) || /Pinterest/.test(ua) || /LinkedIn/.test(ua) || /\bwv\b/.test(ua);
}

/* Shared utility: load Razorpay SDK */
export async function loadRazorpayScript() {
  if (typeof window === "undefined") return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).Razorpay) return true;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Razorpay failed to load"));
    document.body.appendChild(script);
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Boolean((window as any).Razorpay);
}

/**
 * All event-page handler functions extracted from EventPageClient.
 * Accepts the full hook state from useEventPage and returns handler callbacks.
 */
export function useEventHandlers(state: ReturnType<typeof useEventPage>) {
  const toaster = useToast();

  const {
    event, eventKey, eventId, inviteId,
    getToken, isLoaded, isSignedIn, user,
    queryClient,
    rsvpState, setRsvpState, setRsvpError, setRsvpStatus,
    setSuperGuest,
    inviteInput, setInviteInput, setInviteStatus, setInviteError,
    setShowAuthModal, setAuthModalMessage,
    setShowProfileGate,
    setShowInAppBrowserWarning, setCopiedLink,
    setAttendees, setAttendeesLoading,
    setReportStatus, setReportError,
    reportReason, reportNotes, reportEmail,
    ticketPickerOpen, setTicketPickerOpen,
    selectedTickets, setSelectedTickets,
    promoCode, setPromoCode, promoValidation, setPromoValidation, setPromoValidating,
    setDummyPaymentOpen, setDummyPaymentOrder, setDummyPaymentError, setDummyPaymentLoading,
    setCashOnlyOpen,
    failedPaymentInfo, setFailedPaymentInfo, setRetryingVerification,
    checkoutDebounced, setCheckoutDebounced,
    setCheckoutStep, formResponses, setFormResponses, formResponsesRef, pendingOrder, setPendingOrder,
    setTicketModalOpen, setTicketQrUrl, setTicketCode, setTicketRsvpId,
    setPurchasedTickets,
    setShowBuyerForm, setFormFields, setPendingTicketForForm,
    rsvpInFlightRef, checkoutInFlightRef, confirmPaymentInFlightRef,
    isPaidEvent, needsRequest, canRsvp,
    handleToggleSave,
    dbEmail,
    savedSocialLinks,
  } = state;

  // True when the user's saved profile already covers every mandatory platform
  // for this event — gate is unnecessary, skip directly to the action.
  const socialsAlreadySatisfied = (() => {
    const required = (event?.requiredSocialPlatforms ?? []) as string[];
    const mandatory = (event?.mandatorySocialPlatforms ?? []) as string[];
    const minRequired = (event?.minRequiredSocials ?? 1) as number;
    if (required.length === 0) return true;
    const saved = (savedSocialLinks ?? {}) as Record<string, string>;
    const allMandatoryFilled = mandatory.every((p) => saved[p]?.trim());
    const filledCount = required.reduce(
      (n, p) => n + (saved[p]?.trim() ? 1 : 0),
      0,
    );
    return allMandatoryFilled && filledCount >= minRequired;
  })();

  const pendingActionRef = useRef<"rsvp" | "checkout" | null>(null);
  const profileGateConfirmedRef = useRef(false);
  // After ProfileGate completes with a new email, this holds the fresh value so
  // the retried handleRsvp/handleTicketCheckout bypasses the stale `dbEmail` closure.
  const freshEmailOverrideRef = useRef<string | null>(null);
  const guestCheckoutInProgressRef = useRef(false);
  // US-6.3: set when a signed-in soft-account user chooses "Continue as guest"
  // from the ProfileGate, so the checkout takes the guest (sessionToken) path
  // even though Clerk reports them as signed in.
  const guestOverrideRef = useRef(false);

  const showTicketQr = async (code: string | null, rsvpId: string | null) => {
    if (!code) return;
    setTicketCode(code);
    setTicketRsvpId(rsvpId);
    try {
      let qrValue = code;
      try {
        const qrTokenData = await apiFetchAuthed<{ token: string }>(`events/${eventKey}/qr-token`, {}, () => getToken());
        if (qrTokenData?.token) qrValue = qrTokenData.token;
      } catch { /* fallback to raw ticket code */ }
      const qr = await QRCode.toDataURL(qrValue, { margin: 1, width: 200, color: { dark: "#000000", light: "#ffffff" } });
      setTicketQrUrl(qr);
      setTicketModalOpen(true);
    } catch { /* QR generation failed */ }
  };

  const applyRsvpStatus = async (status: RsvpStatus, tc?: string | null, rid?: string | null, formPending?: boolean, tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[], submittedFormResponses?: Record<string, unknown>) => {
    setRsvpStatus(status);
    if (status === "CONFIRMED") {
      setRsvpState("confirmed");

      const hasPreCollectedForm = submittedFormResponses && Object.keys(submittedFormResponses).length > 0;

      // Submit form responses collected before payment (if any)
      if (hasPreCollectedForm && eventKey) {
        // Find the rsvpId: prefer from individual tickets, fall back to rid
        const rsvpIdForForm = (tickets && tickets.length > 0 ? tickets[0].rsvpId : rid) ?? null;
        if (rsvpIdForForm) {
          try {
            await apiFetchAuthed(`events/${eventKey}/form-response`, {
              method: "POST",
              body: JSON.stringify({ responses: submittedFormResponses, rsvpId: rsvpIdForForm }),
            }, () => getToken());
          } catch { /* form submission failed silently — ticket is still valid */ }
        }
        setFormResponses({});
      }

      // For non-checkout flows (e.g. free RSVP without ticket picker), show
      // the buyer form modal post-confirmation if the API flagged formPending
      if (!hasPreCollectedForm && formPending && eventKey) {
        setPendingTicketForForm({ code: tc ?? null, rsvpId: rid ?? null, tickets });
        try {
          const formDef = await apiFetchAuthed<{ fields: { id: string; type: string; label: string; required: boolean; placeholder?: string; helpText?: string; options?: string[]; validation?: Record<string, unknown>; sortOrder: number }[] }>(`events/${eventKey}/form`, {}, () => getToken());
          if (formDef?.fields?.length) { setFormFields(formDef.fields); setShowBuyerForm(true); return; }
        } catch { /* fall through */ }
        setPendingTicketForForm(null);
      }

      if (tickets && tickets.length > 0) {
        setPurchasedTickets(tickets);
        setTicketModalOpen(true);
      } else if (tc) {
        showTicketQr(tc, rid ?? null);
      }
    } else if (status === "APPROVED") setRsvpState("approved");
    else if (status === "WAITLIST") setRsvpState("waitlist");
    else if (status === "PENDING") setRsvpState("pending");
    else if (status === "REJECTED") setRsvpState("rejected");
    else if (status === "CANCELLED") setRsvpState("cancelled");
    else setRsvpState("confirmed");
  };

  const handleRsvp = async () => {
    if (!event || !isLoaded) return;
    if (rsvpInFlightRef.current) return;
    rsvpInFlightRef.current = true;
    try {
      if (isPaidEvent && !needsRequest) {
        if (isInAppBrowser()) { setShowInAppBrowserWarning(true); return; }
        setTicketPickerOpen(true); return;
      }
      if (!isSignedIn) { setTicketPickerOpen(true); setCheckoutStep("guest-info"); return; }
      const email = freshEmailOverrideRef.current || dbEmail || user?.primaryEmailAddress?.emailAddress || "";
      if (!guestCheckoutInProgressRef.current && (!email || email.endsWith("@phone.hopp.co.in"))) {
        pendingActionRef.current = "rsvp";
        setShowProfileGate(true);
        return;
      }

      // Check if social profiles are required — skip if already saved on profile
      const requiredSocials = event.requiredSocialPlatforms ?? [];
      if (!guestCheckoutInProgressRef.current && requiredSocials.length > 0 && !profileGateConfirmedRef.current && !socialsAlreadySatisfied) {
        pendingActionRef.current = "rsvp";
        setShowProfileGate(true);
        return;
      }
      profileGateConfirmedRef.current = false;
      freshEmailOverrideRef.current = null;

      setRsvpState("loading");
      setRsvpError(null);
      const stop = log.time("rsvp");
      try {
        const savedSocials = typeof window !== "undefined" ? sessionStorage.getItem("hopp_social_handles") : null;
        const socials = savedSocials ? JSON.parse(savedSocials) : {};
        const rsvpBody: Record<string, unknown> = { email };
        if (inviteId) rsvpBody.inviteId = inviteId;
        // Prefer fresh handles from the modal (sessionStorage), fall back to the
        // saved profile so skipped-gate RSVPs still carry the social handles.
        const igHandle = socials.instagramHandle || savedSocialLinks?.instagram;
        const xHandleVal = socials.xHandle || savedSocialLinks?.x;
        const snapHandle = socials.snapchatHandle || savedSocialLinks?.snapchat;
        if (igHandle) rsvpBody.instagramHandle = igHandle;
        if (xHandleVal) rsvpBody.xHandle = xHandleVal;
        if (snapHandle) rsvpBody.snapchatHandle = snapHandle;

        const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean }>(
          `events/${eventKey}/rsvp`,
          { method: "POST", body: JSON.stringify(rsvpBody) },
          () => getToken(),
        );
        stop();
        const returnedStatus = data?.status ?? "CONFIRMED";
        // Clean up social handles from sessionStorage after successful RSVP
        try { sessionStorage.removeItem("hopp_social_handles"); sessionStorage.removeItem("hopp_event_social_req"); } catch { /* ignore */ }
        applyRsvpStatus(returnedStatus, data?.ticketCode, data?.rsvpId, data?.formPending, undefined, formResponsesRef.current);
        if ((returnedStatus === "APPROVED" || returnedStatus === "CONFIRMED") && event && "restricted" in event && event.restricted) {
          apiFetchAuthed<PublicEvent>(`events/${eventId}`, { cache: "no-store" }, () => getToken())
            .then((fullEvent) => queryClient.setQueryData([ENV_PREFIX, "event", eventId], fullEvent))
            .catch(() => {});
        }
        if (returnedStatus === "PENDING") toaster.success("Access requested! The host will review your request.");
        else if (returnedStatus === "APPROVED") { toaster.success("You're approved! Complete payment to secure your spot."); if (isPaidEvent) setTicketPickerOpen(true); }
        else toaster.success("You're on the list!");
      } catch (err: unknown) {
        stop();
        setRsvpState("error");
        toaster.error(err instanceof Error ? err.message : "Unable to RSVP");
        setRsvpError(err instanceof Error ? err.message : "Unable to RSVP for this event.");
      }
    } finally { rsvpInFlightRef.current = false; }
  };

  const handleTicketCheckout = async () => {
    if (checkoutInFlightRef.current) return;
    if (!event || !isLoaded || checkoutDebounced) return;
    // Only paid events need a real browser (Razorpay). Free bookings have no
    // payment step, so don't block them inside Instagram/etc. in-app browsers.
    if (isPaidEvent && isInAppBrowser()) { setTicketPickerOpen(false); setShowInAppBrowserWarning(true); return; }
    checkoutInFlightRef.current = true;
    try {
    // Deterministic routing: once the guest form has stored a sessionToken,
    // always finish via the guest path — even if the OTP sign-in flipped
    // isSignedIn to true between onComplete firing (avoids a race that would
    // wrongly bounce a just-verified guest to ProfileGate).
    const pendingGuestRaw = typeof window !== "undefined"
      ? sessionStorage.getItem("hopp_guest_info")
      : null;
    let hasPendingGuestSession = false;
    try { hasPendingGuestSession = !!(pendingGuestRaw && JSON.parse(pendingGuestRaw)?.sessionToken); } catch { /* ignore */ }
    if (!isSignedIn || guestOverrideRef.current || hasPendingGuestSession) {
      // Guest path per US-2.2/US-4.1: if the user has already completed the
      // inline phone-OTP-name-email form, proceed directly to guest payment
      // (no Clerk account required). Otherwise open the guest-info form.
      // guestOverrideRef (US-6.3) also routes a signed-in soft-account user
      // here when they pick "Continue as guest" instead of adding an email.
      const rawGuestInfo = typeof window !== "undefined"
        ? sessionStorage.getItem("hopp_guest_info")
        : null;
      let guestInfo: { sessionToken?: string; name?: string; email?: string; phone?: string } | null = null;
      try {
        guestInfo = rawGuestInfo ? JSON.parse(rawGuestInfo) : null;
      } catch { guestInfo = null; }

      if (!guestInfo?.sessionToken) {
        setCheckoutStep("guest-info");
        return;
      }

      // Events without real ticketCategories use the backend's legacy
      // single-tier path (derives price from event.ticketPriceCents). The UI
      // synthesizes a "general" fallback entry in selectedTickets for these
      // events — don't filter it out, just pass it through and omit
      // paymentItems so the backend takes the fallback branch.
      const hasRealCategories = (event.ticketCategories ?? []).some((c) => c.type !== "COVER_CHARGE");
      const guestCheckoutItems = hasRealCategories
        ? (selectedTickets ?? []).filter((t) =>
            event.ticketCategories?.some((c) => c.id === t.id && c.type !== "COVER_CHARGE"),
          )
        : (selectedTickets ?? []);
      const guestFirst = guestCheckoutItems[0];
      if (!guestFirst) { setRsvpState("error"); setRsvpError("Please select a ticket tier."); return; }
      const anyGuestSoldOut = hasRealCategories && guestCheckoutItems.some((t) => event.ticketCategories?.find((c) => c.id === t.id)?.isSoldOut);
      if (!canRsvp) { setRsvpState("error"); setRsvpError("This event is no longer accepting RSVPs."); return; }
      if (anyGuestSoldOut) { setRsvpState("error"); setRsvpError("A selected ticket tier is sold out."); return; }
      // Client-side max-per-order guard (backend enforces too) for a clearer message.
      const guestTotalQty = guestCheckoutItems.reduce((n, t) => n + (t.quantity ?? 0), 0);
      const maxPerOrder = event.maxTicketsPerOrder ?? 0;
      if (maxPerOrder > 0 && guestTotalQty > maxPerOrder) {
        setRsvpState("error");
        setRsvpError(`You can book at most ${maxPerOrder} ticket${maxPerOrder > 1 ? "s" : ""} per order.`);
        return;
      }

      setRsvpState("loading"); setRsvpError(null);
      const tGuest = performance.now();
      try {
        const createOrderBody: Record<string, unknown> = {
          sessionToken: guestInfo.sessionToken,
          name: guestInfo.name,
          email: guestInfo.email,
          promoCode: promoValidation?.valid ? promoCode.trim() : undefined,
        };
        if (hasRealCategories) {
          createOrderBody.paymentItems = guestCheckoutItems.map((t) => ({ ticketCategoryId: t.id, quantity: t.quantity }));
        } else {
          createOrderBody.quantity = guestFirst.quantity;
        }
        const order = await apiFetch<{
          free?: boolean; status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[];
          keyId: string; orderId: string; amount: number; currency: string; paymentType?: string;
          priceBreakdown?: { ticketBaseCents: number; ticketOnlyBaseCents?: number; ticketTotalCents?: number; coverChargeCents: number; ticketGstCents: number; platformFeeBaseCents: number; platformFeeGstCents: number; platformFeeGstRate?: number; platformFeeRate?: number; discountCents: number; finalAmountCents: number; gstRate?: number };
        }>(
          "checkout-session/create-order",
          { method: "POST", body: JSON.stringify(createOrderBody) },
        );
        const elapsed = performance.now() - tGuest;
        if (elapsed > 3000) console.warn(`[checkout-timing] GUEST checkout took ${elapsed.toFixed(0)}ms (>3s)`, { eventKey, orderId: order.orderId });
        // Free / zero-amount booking: the backend already created the RSVP —
        // there's no Razorpay step, so show the ticket directly (US-3.x).
        if (order.free) {
          if (typeof window !== "undefined") sessionStorage.removeItem("hopp_guest_info");
          setTicketPickerOpen(false);
          setSelectedTickets([]); setPromoCode("");
          applyRsvpStatus(order.status ?? "CONFIRMED", order.ticketCode, order.rsvpId, order.formPending, order.tickets, formResponsesRef.current);
          setRsvpState("idle");
          return;
        }
        setPendingOrder(order); setCheckoutStep("review"); setRsvpState("idle");
        return;
      } catch (err) {
        // Session expired or invalidated server-side → clear the stale
        // sessionToken and push the user back to the guest-info form so they
        // can mint a fresh session via phone OTP. Prefill will kick in
        // automatically for returning phones (US-6.1), so the interruption
        // is ~30 seconds, not a full restart.
        const msg = err instanceof Error ? err.message.toLowerCase() : "";
        const isExpired =
          msg.includes("expired") ||
          msg.includes("invalid or expired") ||
          msg.includes("phone not verified");
        if (isExpired && typeof window !== "undefined") {
          // The 30-min checkout session lapsed. Clear it and tell the user how
          // to recover (re-opening the picker remounts the form at the phone
          // step) instead of silently leaving them stuck on the email screen.
          sessionStorage.removeItem("hopp_guest_info");
          setRsvpState("error");
          setRsvpError("Your booking session timed out. Please close and start again to re-verify your phone.");
          setCheckoutStep("guest-info");
          return;
        }
        setRsvpState("error"); setRsvpError(err instanceof Error ? err.message : "Unable to start checkout.");
        return;
      }
    }
    const email = freshEmailOverrideRef.current || dbEmail || user?.primaryEmailAddress?.emailAddress || "";
    if (!email || email.endsWith("@phone.hopp.co.in")) {
      setTicketPickerOpen(false);
      pendingActionRef.current = "checkout";
      setShowProfileGate(true);
      return;
    }

    // Check if social profiles are required — skip if already saved on profile
    const requiredSocials = event.requiredSocialPlatforms ?? [];
    if (requiredSocials.length > 0 && !profileGateConfirmedRef.current && !socialsAlreadySatisfied) {
      setTicketPickerOpen(false);
      pendingActionRef.current = "checkout";
      setShowProfileGate(true);
      return;
    }
    profileGateConfirmedRef.current = false;
    freshEmailOverrideRef.current = null;

    // Show buyer form step if event has a custom form and responses not yet collected
    if (Object.keys(formResponsesRef.current).length === 0 && eventKey) {
      // If formFields are already loaded and non-empty, show form step
      if (state.formFields.length > 0) {
        setCheckoutStep("form");
        return;
      }
      // If formFields not yet loaded, try fetching them
      if (state.formFields.length === 0) {
        try {
          const formDef = await apiFetchAuthed<{ fields: { id: string; type: string; label: string; required: boolean; placeholder?: string; helpText?: string; options?: string[]; validation?: Record<string, unknown>; sortOrder: number }[] }>(`events/${eventKey}/form`, {}, () => getToken());
          if (formDef?.fields?.length) {
            setFormFields(formDef.fields);
            setCheckoutStep("form");
            return;
          }
        } catch { /* no form — proceed to checkout */ }
      }
    }

    setCheckoutDebounced(true);
    setTimeout(() => setCheckoutDebounced(false), 2000);

    const checkoutItems = (selectedTickets ?? []).filter((t) =>
      event.ticketCategories?.some((c) => c.id === t.id && c.type !== "COVER_CHARGE"),
    );
    const firstItem = checkoutItems[0];
    if (!firstItem) { setRsvpState("error"); setRsvpError("Please select a ticket tier."); return; }
    const allTiersFree = checkoutItems.every((t) => {
      const cat = event.ticketCategories?.find((c) => c.id === t.id);
      return cat && (!cat.priceCents || cat.priceCents <= 0);
    });
    const tierIsFree = allTiersFree;
    const anySoldOut = checkoutItems.some((t) => event.ticketCategories?.find((c) => c.id === t.id)?.isSoldOut);

    if (!canRsvp) { setRsvpState("error"); setRsvpError("This event is no longer accepting RSVPs."); return; }
    if (anySoldOut) { setRsvpState("error"); setRsvpError("A selected ticket tier is sold out."); return; }

    if (tierIsFree && checkoutItems.length === 1) {
      // profileGateConfirmedRef was already checked and cleared above (line ~308-314);

      setRsvpState("loading"); setRsvpError(null);
      const t0 = performance.now();
      try {
        const savedSocialsCheckout = typeof window !== "undefined" ? sessionStorage.getItem("hopp_social_handles") : null;
        const socialsCheckout = savedSocialsCheckout ? JSON.parse(savedSocialsCheckout) : {};
        const freeRsvpBody: Record<string, unknown> = { email, ticketCategoryId: firstItem.id, quantity: firstItem.quantity };
        const igHandleCheckout = socialsCheckout.instagramHandle || savedSocialLinks?.instagram;
        const xHandleCheckout = socialsCheckout.xHandle || savedSocialLinks?.x;
        const snapHandleCheckout = socialsCheckout.snapchatHandle || savedSocialLinks?.snapchat;
        if (igHandleCheckout) freeRsvpBody.instagramHandle = igHandleCheckout;
        if (xHandleCheckout) freeRsvpBody.xHandle = xHandleCheckout;
        if (snapHandleCheckout) freeRsvpBody.snapchatHandle = snapHandleCheckout;

        const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] }>(
          `events/${eventKey}/rsvp`, { method: "POST", body: JSON.stringify(freeRsvpBody) }, () => getToken());
        const elapsed = performance.now() - t0;
        if (elapsed > 3000) console.warn(`[checkout-timing] FREE rsvp took ${elapsed.toFixed(0)}ms (>3s)`, { eventKey, ticketCategoryId: firstItem.id });
        else console.log(`[checkout-timing] FREE rsvp completed in ${elapsed.toFixed(0)}ms`, { eventKey });
        setTicketPickerOpen(false);
        setSelectedTickets([]); setPromoCode("");
        try { sessionStorage.removeItem("hopp_social_handles"); sessionStorage.removeItem("hopp_event_social_req"); } catch { /* ignore */ }
        applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
        return;
      } catch (err) {
        const elapsed = performance.now() - t0;
        console.warn(`[checkout-timing] FREE rsvp FAILED after ${elapsed.toFixed(0)}ms`, { eventKey, error: err instanceof Error ? err.message : err });
        if (err instanceof AuthExpiredError) { setTicketPickerOpen(false); setRsvpState("idle"); setAuthModalMessage("Your session expired. Please sign in again to continue."); setShowAuthModal(true); return; }
        setRsvpState("error"); setRsvpError(err instanceof Error ? err.message : "Unable to RSVP."); return;
      }
    }

    setRsvpState("loading"); setRsvpError(null);
    const t0 = performance.now();
    try {
      const order = await apiFetchAuthed<{
        keyId: string; orderId: string; amount: number; currency: string; paymentType?: string; message?: string; status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null;
        priceBreakdown?: { ticketBaseCents: number; ticketOnlyBaseCents?: number; ticketTotalCents?: number; coverChargeCents: number; ticketGstCents: number; platformFeeBaseCents: number; platformFeeGstCents: number; platformFeeGstRate?: number; platformFeeRate?: number; discountCents: number; finalAmountCents: number; gstRate?: number };
      }>(
        `events/${eventKey}/checkout`,
        { method: "POST", headers: { "x-idempotency-key": `${eventKey}-${user?.id ?? "anon"}-${Date.now()}` }, body: JSON.stringify({ email, paymentItems: checkoutItems.map((t) => ({ ticketCategoryId: t.id, quantity: t.quantity })), promoCode: promoValidation?.valid ? promoCode.trim() : undefined }) },
        () => getToken(),
      );
      const elapsed = performance.now() - t0;
      if (elapsed > 3000) console.warn(`[checkout-timing] PAID checkout took ${elapsed.toFixed(0)}ms (>3s)`, { eventKey, orderId: order.orderId });
      else console.log(`[checkout-timing] PAID checkout completed in ${elapsed.toFixed(0)}ms`, { eventKey, orderId: order.orderId });
      if (order.paymentType === "CASH_ONLY") { setTicketPickerOpen(false); setRsvpState("idle"); setCashOnlyOpen(true); return; }
      if (order.amount <= 0) {
        setTicketPickerOpen(false);
        if (order.keyId === "dummy") { setRsvpState("idle"); setDummyPaymentOrder({ orderId: order.orderId, currency: order.currency }); setDummyPaymentOpen(true); return; }
        const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] }>(
          `events/${eventKey}/checkout/verify`, { method: "POST", body: JSON.stringify({ orderId: order.orderId, paymentId: "free", signature: "free" }) }, () => getToken());
        setSelectedTickets([]); setPromoCode("");
        applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
        return;
      }
      setPendingOrder(order); setCheckoutStep("review"); setRsvpState("idle");
    } catch (err) {
      const elapsed = performance.now() - t0;
      console.warn(`[checkout-timing] PAID checkout FAILED after ${elapsed.toFixed(0)}ms`, { eventKey, error: err instanceof Error ? err.message : err });
      if (err instanceof AuthExpiredError) { setTicketPickerOpen(false); setRsvpState("idle"); setAuthModalMessage("Your session expired. Please sign in again to continue."); setShowAuthModal(true); return; }
      setRsvpState("error"); setRsvpError(err instanceof Error ? err.message : "Unable to start checkout.");
    }
    } finally { checkoutInFlightRef.current = false; }
  };

  const handleConfirmPayment = async () => {
    if (!pendingOrder || !event) return;
    if (confirmPaymentInFlightRef.current) return;
    confirmPaymentInFlightRef.current = true;

    // Guest path: a hopp_guest_info session exists. Use the public
    // /checkout-session/verify-payment endpoint rather than the authenticated
    // events/:id/checkout/verify path. We key off the sessionToken alone (not
    // !isSignedIn) so a signed-in soft-account user who chose "Continue as
    // guest" (US-6.3) also completes via the guest endpoint, which the backend
    // accepts for sessions bound to a Clerk user.
    const rawGuestInfo = typeof window !== "undefined"
      ? sessionStorage.getItem("hopp_guest_info")
      : null;
    let guestInfo: { sessionToken?: string; name?: string; email?: string; phone?: string } | null = null;
    try { guestInfo = rawGuestInfo ? JSON.parse(rawGuestInfo) : null; } catch { guestInfo = null; }
    const isGuestPayment = Boolean(guestInfo?.sessionToken);

    const email = isGuestPayment
      ? (guestInfo?.email ?? "")
      : (user?.primaryEmailAddress?.emailAddress ?? "");
    if (!isGuestPayment && !email) { confirmPaymentInFlightRef.current = false; return; }
    setRsvpState("loading"); setRsvpError(null);
    try {
      if (isInAppBrowser()) { setRsvpState("idle"); setShowInAppBrowserWarning(true); return; }
      const loaded = await loadRazorpayScript();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!loaded || !(window as any).Razorpay) {
        if (isInAppBrowser()) { setRsvpState("idle"); setShowInAppBrowserWarning(true); return; }
        throw new Error("Payment system failed to load.");
      }
      const displayAmount = pendingOrder.priceBreakdown?.finalAmountCents ?? pendingOrder.amount;
      const prefillContact = isGuestPayment
        ? (guestInfo?.phone || undefined)
        : (user?.primaryPhoneNumber?.phoneNumber || undefined);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const razorpay = new (window as any).Razorpay({
        key: pendingOrder.keyId, amount: displayAmount, currency: pendingOrder.currency,
        name: "Hopp", description: event.title, order_id: pendingOrder.orderId,
        prefill: { email, contact: prefillContact },
        config: { display: { blocks: { utib: { name: "Pay using UPI", instruments: [{ method: "upi" }] } }, sequence: ["block.utib"], preferences: { show_default_blocks: true } } },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handler: async (response: any) => {
          try {
            type VerifyResult = { status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] };
            const data = isGuestPayment
              ? await apiFetch<VerifyResult>(
                  "checkout-session/verify-payment",
                  { method: "POST", body: JSON.stringify({ sessionToken: guestInfo!.sessionToken, orderId: response.razorpay_order_id, paymentId: response.razorpay_payment_id, signature: response.razorpay_signature }) },
                )
              : await apiFetchAuthed<VerifyResult>(
                  `events/${eventKey}/checkout/verify`, { method: "POST", body: JSON.stringify({ orderId: response.razorpay_order_id, paymentId: response.razorpay_payment_id, signature: response.razorpay_signature }) }, () => getToken());
            setPendingOrder(null);
            setCheckoutStep("select");
            setSelectedTickets([]);
            if (isGuestPayment && typeof window !== "undefined") {
              sessionStorage.removeItem("hopp_guest_info");
            }
            guestOverrideRef.current = false;
            applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
          } catch (err) {
            setRsvpState("error"); setRsvpError("Payment was captured but RSVP failed.");
            setFailedPaymentInfo({ orderId: response.razorpay_order_id, paymentId: response.razorpay_payment_id, signature: response.razorpay_signature });
          }
        },
        modal: {
          ondismiss: () => {
            setRsvpState("idle");
            setCheckoutStep("select");
            setPendingOrder(null);
            setTicketPickerOpen(true);
          },
        },
      });
      setTicketPickerOpen(false);
      razorpay.open();
    } catch (err) {
      if (err instanceof AuthExpiredError) { setTicketPickerOpen(false); setRsvpState("idle"); setAuthModalMessage("Your session expired. Please sign in again to continue."); setShowAuthModal(true); return; }
      setRsvpState("error"); setRsvpError(err instanceof Error ? err.message : "Payment failed to start.");
    } finally { confirmPaymentInFlightRef.current = false; }
  };

  const handleRetryVerification = async () => {
    if (!failedPaymentInfo || !event) return;
    setRetryingVerification(true); setRsvpError(null);
    try {
      const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] }>(
        `events/${eventKey}/checkout/verify`, { method: "POST", body: JSON.stringify({ orderId: failedPaymentInfo.orderId, paymentId: failedPaymentInfo.paymentId, signature: failedPaymentInfo.signature }) }, () => getToken());
      setFailedPaymentInfo(null);
      applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
    } catch { setRsvpError("Verification still failing. Please contact support."); }
    finally { setRetryingVerification(false); }
  };

  const handleApplyPromo = async () => {
    if (!event || !promoCode.trim()) return;
    setPromoValidating(true); setPromoValidation(null);
    try {
      const result = await apiFetch<{ valid: boolean; discountCents?: number; discountType?: string; discountValue?: number; finalAmount?: number; currency?: string; reason?: string }>(
        `events/${eventKey}/promo/validate`, { method: "POST", body: JSON.stringify({ code: promoCode.trim() }) });
      setPromoValidation(result);
    } catch (err) { setPromoValidation({ valid: false, reason: err instanceof Error ? err.message : undefined }); }
    finally { setPromoValidating(false); }
  };

  const handleDummyPaymentSubmit = async (cardName: string, cardNumber: string, cardExpiry: string, cardCvc: string) => {
    if (!event || !state.dummyPaymentOrder) return;
    if (!isSignedIn) { setDummyPaymentOpen(false); setShowAuthModal(true); return; }
    setDummyPaymentError(null); setDummyPaymentLoading(true); setRsvpState("loading");
    try {
      const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] }>(
        `events/${eventKey}/checkout/verify`, { method: "POST", body: JSON.stringify({ orderId: state.dummyPaymentOrder.orderId, paymentId: "dummy", signature: "dummy" }) }, () => getToken());
      setDummyPaymentOpen(false); setDummyPaymentOrder(null);
      applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
      toaster.success("Payment confirmed! You're in.");
    } catch (err) {
      setRsvpState("error"); setDummyPaymentError(err instanceof Error ? err.message : "Unable to process dummy payment.");
    } finally { setDummyPaymentLoading(false); }
  };

  const handleSendInvites = async () => {
    if (!event || !isLoaded) return;
    if (!isSignedIn) { setShowAuthModal(true); return; }
    if (!state.canInvite) return;
    const raw = inviteInput.split(/[,\s]+/g).map((entry) => entry.trim()).filter(Boolean);
    if (raw.length === 0) { setInviteError("Add at least one email to invite."); return; }
    setInviteStatus("sending"); setInviteError(null);
    try {
      const response = await apiFetchAuthed<{ remaining?: number }>(`events/${event.id}/invites`, { method: "POST", body: JSON.stringify({ emails: raw }) }, () => getToken());
      if (typeof response?.remaining === "number") {
        setSuperGuest((prev) => prev ? { ...prev, invitesLeft: response.remaining, invitesSent: typeof prev.invitesSent === "number" ? prev.invitesSent + raw.length : prev.invitesSent } : prev);
      }
      setInviteInput(""); setInviteStatus("sent");
      toaster.success(`Invite${raw.length > 1 ? "s" : ""} sent`);
      setTimeout(() => setInviteStatus("idle"), 1500);
    } catch (err: unknown) {
      setInviteStatus("idle"); toaster.error(err instanceof Error ? err.message : "Unable to send invites");
      setInviteError(err instanceof Error ? err.message : "Unable to send invites.");
    }
  };

  const handleCancelRsvp = async () => {
    if (!event || !isLoaded || !isSignedIn) return;
    setRsvpState("loading"); setRsvpError(null);
    try {
      const data = await apiFetchAuthed<{ status?: RsvpStatus }>(`events/${event.id}/rsvp/cancel`, { method: "POST" }, () => getToken());
      const status = data?.status ?? "CANCELLED";
      setRsvpStatus(status); setRsvpState(status === "CANCELLED" ? "cancelled" : "idle");
      toaster.info("RSVP cancelled");
    } catch (err: unknown) {
      setRsvpState("error"); toaster.error(err instanceof Error ? err.message : "Unable to cancel RSVP");
      setRsvpError(err instanceof Error ? err.message : "Unable to cancel RSVP.");
    }
  };

  const handleOpenAttendees = async () => {
    if (!event || state.attendeesLoading) return;
    state.setAttendeesOpen(true);
    if (state.attendees.length > 0) return;
    setAttendeesLoading(true);
    try {
      const data = await apiFetch<{ id: string; name: string; imageUrl: string | null }[]>(`events/${event.id}/attendees`);
      setAttendees(data);
    } catch { /* ignore */ }
    finally { setAttendeesLoading(false); }
  };

  const handleSubmitReport = async () => {
    if (!event || state.reportStatus === "submitting") return;
    const reason = reportReason.trim();
    if (!reason) { setReportError("Please select a reason."); return; }
    setReportStatus("submitting"); setReportError(null);
    try {
      await apiFetch(`reports`, { method: "POST", body: JSON.stringify({ targetType: "EVENT", targetId: event.id, reason, notes: reportNotes.trim() || undefined, email: reportEmail.trim() || undefined }) });
      setReportStatus("sent"); toaster.success("Report submitted, thank you");
    } catch (err: unknown) {
      setReportStatus("error"); toaster.error(err instanceof Error ? err.message : "Unable to submit report");
      setReportError(err instanceof Error ? err.message : "Unable to submit report.");
    }
  };

  const handleCashOnlyConfirm = async () => {
    setCashOnlyOpen(false); setRsvpState("loading");
    try {
      const data = await apiFetchAuthed<{ status?: RsvpStatus; ticketCode?: string | null; rsvpId?: string | null; formPending?: boolean; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] }>(
        `events/${eventKey}/rsvp`, { method: "POST", body: JSON.stringify({ email: user?.primaryEmailAddress?.emailAddress ?? "", ticketCategoryId: selectedTickets[0]?.id ?? undefined }) }, () => getToken());
      applyRsvpStatus(data?.status ?? "CONFIRMED", data?.ticketCode, data?.rsvpId, data?.formPending, data?.tickets, formResponsesRef.current);
      toaster.success("You're on the list! Pay at the door.");
    } catch (err) { setRsvpState("error"); setRsvpError(err instanceof Error ? err.message : "Unable to RSVP."); }
  };

  const handleSaveToggle = () => {
    const result = handleToggleSave();
    if (result && typeof result === "object" && "needsAuth" in result) setShowAuthModal(true);
  };

  const handleProfileGateComplete = useCallback(async (socials: { instagramHandle?: string; xHandle?: string; snapchatHandle?: string; emailUpdated?: string }) => {
    // Save socials to sessionStorage for RSVP POST
    if (Object.values(socials).some(v => v?.trim())) {
      sessionStorage.setItem("hopp_social_handles", JSON.stringify(socials));
    }
    // If email was updated (phone-OTP merge), refetch /me to update dbEmail
    if (socials.emailUpdated) {
      try {
        const meData = await apiFetchAuthed<{ email: string | null }>(
          `events/${eventId}/me`, {}, () => getToken(),
        );
        if (meData?.email) {
          // Update dbEmail state so future renders see the new email
          state.setDbEmail?.(meData.email);
          // Bypass the stale closure in the auto-retried handler below.
          freshEmailOverrideRef.current = meData.email;
        } else {
          // Fall back to whatever the modal submitted.
          freshEmailOverrideRef.current = socials.emailUpdated;
        }
      } catch {
        // /me fetch failed — still use the modal's submitted email so retry doesn't loop.
        freshEmailOverrideRef.current = socials.emailUpdated;
      }
    }
    setShowProfileGate(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    // Mark as confirmed so the retry skips the modal
    profileGateConfirmedRef.current = true;
    // Auto-retry the pending action
    if (action === "rsvp") {
      setTimeout(() => handleRsvp(), 200);
    } else if (action === "checkout") {
      setTicketPickerOpen(true);
      setTimeout(() => handleTicketCheckout(), 200);
    }
  }, [handleRsvp, handleTicketCheckout, setShowProfileGate, eventId, getToken, state, setTicketPickerOpen]);

  const handleProfileGateClose = useCallback(() => {
    pendingActionRef.current = null;
    guestOverrideRef.current = false;
    setShowProfileGate(false);
  }, [setShowProfileGate]);

  // US-6.3: signed-in soft-account user opts to book as a guest instead of
  // completing their profile. Route them into the guest-info step; on
  // completion handleTicketCheckout takes the guest payment branch because
  // guestOverrideRef bypasses the isSignedIn guard.
  const handleContinueAsGuest = useCallback(() => {
    pendingActionRef.current = null;
    guestOverrideRef.current = true;
    setShowProfileGate(false);
    setTicketPickerOpen(true);
    setCheckoutStep("guest-info");
  }, [setShowProfileGate, setTicketPickerOpen, setCheckoutStep]);

  return {
    showTicketQr,
    applyRsvpStatus,
    handleRsvp,
    handleTicketCheckout,
    handleConfirmPayment,
    handleRetryVerification,
    handleApplyPromo,
    handleDummyPaymentSubmit,
    handleSendInvites,
    handleCancelRsvp,
    handleOpenAttendees,
    handleSubmitReport,
    handleCashOnlyConfirm,
    handleSaveToggle,
    handleProfileGateComplete,
    handleProfileGateClose,
    handleContinueAsGuest,
  };
}
